import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { EnrollmentActions } from './enrollment.actions';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { FIRESTORE } from 'src/app/common/firebase/firebase.tokens';
import { Enrollment } from '@enrollhere/classes';
import { Action } from '@ngrx/store';
import { deleteUndefined } from '../../../misc/deleteUndefined';

/**
 * @deprecated NgRx effects with direct Firestore access are deprecated. Use API endpoints instead.
 */
@Injectable()
export class EnrollmentEffects {
  private actions$ = inject(Actions);
  /** @deprecated Direct Firestore access is deprecated. Use API endpoints instead. */
  private firestore = inject(FIRESTORE);

  private mapEnrollmentFromDoc(docSnapshot: {
    id: string;
    data: () => Record<string, unknown>;
  }) {
    return Enrollment.fromJSON({ ...docSnapshot.data(), id: docSnapshot.id });
  }

  private emitLoadEnrollmentsSuccess(
    subscriber: { next: (action: Action) => void },
    snapshot: {
      docs: Array<{ id: string; data: () => Record<string, unknown> }>;
    }
  ) {
    const enrollments = snapshot.docs.map(docSnapshot =>
      this.mapEnrollmentFromDoc(docSnapshot)
    );
    subscriber.next(EnrollmentActions.loadEnrollmentsSuccess({ enrollments }));
  }

  private emitLoadEnrollmentsFailure(
    subscriber: { next: (action: Action) => void },
    error: unknown
  ) {
    subscriber.next(EnrollmentActions.loadEnrollmentsFailure({ error }));
  }

  private emitLoadEnrollmentSuccess(
    subscriber: { next: (action: Action) => void },
    snapshot: { id: string; data: () => Record<string, unknown> }
  ) {
    const enrollment = Enrollment.fromJSON({
      ...snapshot.data(),
      id: snapshot.id,
    });
    subscriber.next(EnrollmentActions.loadEnrollmentSuccess({ enrollment }));
  }

  private emitLoadEnrollmentFailure(
    subscriber: { next: (action: Action) => void },
    error: unknown
  ) {
    subscriber.next(EnrollmentActions.loadEnrollmentFailure({ error }));
  }

  private createAddEnrollmentSuccess(
    enrollment: Enrollment | Partial<Enrollment>,
    id: string
  ) {
    return EnrollmentActions.addEnrollmentSuccess({
      enrollment: Enrollment.fromJSON({ ...enrollment, id }),
    });
  }

  private createRemoveEnrollmentSuccess(enrollmentId: string) {
    return EnrollmentActions.removeEnrollmentSuccess({ enrollmentId });
  }

  private createUpdateEnrollmentSuccess(
    consumerId: string,
    enrollmentId: string,
    enrollment: Partial<Enrollment>
  ) {
    return EnrollmentActions.updateEnrollmentSuccess({
      consumerId,
      enrollmentId,
      enrollment,
    });
  }

  loadEnrollments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EnrollmentActions.loadEnrollments),
      switchMap(() => {
        return new Observable<Action>(subscriber => {
          const unsubscribe = onSnapshot(
            collection(this.firestore, 'consumers', 'enrollments'),
            snapshot => {
              this.emitLoadEnrollmentsSuccess(
                subscriber,
                snapshot as {
                  docs: Array<{
                    id: string;
                    data: () => Record<string, unknown>;
                  }>;
                }
              );
            },
            error => {
              this.emitLoadEnrollmentsFailure(subscriber, error);
            }
          );

          // Provide a way of canceling and disposing the listener
          return unsubscribe;
        }).pipe(
          catchError(error =>
            of({ type: '[Enrollment API] Load Enrollments Error', error })
          )
        );
      })
    );
  });

  loadEnrollment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EnrollmentActions.loadEnrollment),
      switchMap(({ enrollmentId, consumerId }) => {
        return new Observable<Action>(subscriber => {
          const unsubscribe = onSnapshot(
            doc(
              this.firestore,
              'consumers',
              consumerId,
              'enrollments',
              enrollmentId
            ),
            snapshot => {
              this.emitLoadEnrollmentSuccess(
                subscriber,
                snapshot as {
                  id: string;
                  data: () => Record<string, unknown>;
                }
              );
            },
            error => {
              this.emitLoadEnrollmentFailure(subscriber, error);
            }
          );
          return unsubscribe;
        }).pipe(
          catchError(error =>
            of({ type: '[Enrollment API] Load Enrollment Error', error })
          )
        );
      })
    );
  });

  addEnrollment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EnrollmentActions.addEnrollment),
      mergeMap(async ({ consumerId, enrollment }) => {
        try {
          const docRef = await addDoc(
            collection(this.firestore, 'consumers', consumerId, 'enrollments'),
            enrollment
          );
          return this.createAddEnrollmentSuccess(enrollment, docRef.id); // return new enrollment with id
        } catch (error) {
          return EnrollmentActions.addEnrollmentFailure({ error });
        }
      })
    );
  });

  removeEnrollment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EnrollmentActions.removeEnrollment),
      mergeMap(async ({ consumerId, enrollmentId }) => {
        try {
          await deleteDoc(
            doc(
              this.firestore,
              'consumers',
              consumerId,
              'enrollments',
              enrollmentId
            )
          );
          return this.createRemoveEnrollmentSuccess(enrollmentId); // return removed enrollment's id
        } catch (error) {
          return EnrollmentActions.removeEnrollmentFailure({ error });
        }
      })
    );
  });

  updateEnrollment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EnrollmentActions.updateEnrollment),
      mergeMap(async ({ consumerId, enrollmentId, enrollment }) => {
        try {
          await setDoc(
            doc(
              this.firestore,
              'consumers',
              consumerId,
              'enrollments',
              enrollmentId
            ),
            deleteUndefined(enrollment),
            {
              merge: true,
            }
          );
          return this.createUpdateEnrollmentSuccess(
            consumerId,
            enrollmentId,
            enrollment
          ); // return updated enrollment's id and changes
        } catch (error) {
          return EnrollmentActions.updateEnrollmentFailure({ error });
        }
      })
    );
  });
}
